import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, 
  Share2,
  Copy,
  Users,
  CheckCircle,
  AlertCircle,
  MessageCircle,
  Link2,
  Sparkles,
  UserPlus,
  ExternalLink
} from "lucide-react";

export default function InvitePlayersPage({ evaluator }) {
  const navigate = useNavigate();
  const [inviteLink, setInviteLink] = useState("");
  const [inviteToken, setInviteToken] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [recentInvites, setRecentInvites] = useState([]);

  const fetchRecentInvites = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/invites/list`);
      setRecentInvites(res.data.slice(0, 10));
    } catch (error) {
      console.error("Error loading invites:", error);
    }
  }, []);

  useEffect(() => {
    fetchRecentInvites();
  }, [fetchRecentInvites]);

  const generateInviteLink = async () => {
    setIsGenerating(true);
    try {
      const res = await axios.post(`${API}/invites/generate-link`);
      const token = res.data.token;
      const baseUrl = window.location.origin;
      const link = `${baseUrl}/cadastro/${token}`;
      
      setInviteToken(token);
      setInviteLink(link);
      toast.success("Link de convite gerado!");
      fetchRecentInvites();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao gerar link");
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      toast.success("Link copiado!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Erro ao copiar");
    }
  };

  const shareWhatsApp = () => {
    const message = encodeURIComponent(
      `🏐 *VolleyxBrasil*\n\nVocê foi convidado para se cadastrar no sistema de vôlei!\n\nClique no link abaixo para fazer seu cadastro:\n${inviteLink}`
    );
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  if (evaluator?.role !== 'admin' && evaluator?.role !== 'tecnico') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="bg-card/50 border-destructive">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <p className="text-lg font-semibold">Acesso Restrito</p>
            <p className="text-muted-foreground">Apenas administradores e técnicos podem enviar convites.</p>
            <Button onClick={() => navigate('/dashboard')} className="mt-4">
              Voltar ao Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
                data-testid="back-btn"
                className="hover:bg-primary/10"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl md:text-2xl font-bold tracking-tight flex items-center gap-2">
                  <UserPlus className="h-6 w-6 text-primary" />
                  Convidar Jogadores
                </h1>
                <p className="text-sm text-muted-foreground">
                  Gere um link e compartilhe via WhatsApp
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Main Card - Generate Link */}
        <Card className="bg-card/80 backdrop-blur-sm border-border/50 shadow-xl mb-6 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent pointer-events-none" />
          <CardHeader className="relative">
            <CardTitle className="flex items-center gap-2 text-xl">
              <Link2 className="h-5 w-5 text-primary" />
              Link de Cadastro
            </CardTitle>
            <CardDescription>
              Gere um link único para jogadores se cadastrarem no sistema
            </CardDescription>
          </CardHeader>
          <CardContent className="relative space-y-6">
            {/* Generate Button */}
            {!inviteLink && (
              <div className="text-center py-8">
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="h-10 w-10 text-primary" />
                </div>
                <p className="text-muted-foreground mb-6">
                  Clique no botão para gerar um novo link de convite
                </p>
                <Button
                  onClick={generateInviteLink}
                  disabled={isGenerating}
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-8 shadow-lg shadow-primary/25"
                  data-testid="generate-link-btn"
                >
                  {isGenerating ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Gerando...
                    </>
                  ) : (
                    <>
                      <Link2 className="h-5 w-5 mr-2" />
                      Gerar Link de Convite
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Link Generated */}
            {inviteLink && (
              <div className="space-y-4">
                {/* Success Badge */}
                <div className="flex items-center justify-center gap-2 text-green-500">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-medium">Link gerado com sucesso!</span>
                </div>

                {/* Link Display */}
                <div className="bg-secondary/50 rounded-xl p-4 border border-border/50">
                  <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wider font-medium">
                    Link para compartilhar:
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-sm bg-background/80 px-3 py-2 rounded-lg border break-all font-mono">
                      {inviteLink}
                    </code>
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={copyToClipboard}
                      className="flex-shrink-0 h-11 w-11 flex items-center justify-center"
                      data-testid="copy-link-btn"
                    >
                      {copied ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <Copy className="h-5 w-5" />
                      )}
                    </Button>
                  </div>
                </div>

                {/* Share Buttons */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Button
                    onClick={shareWhatsApp}
                    className="bg-green-600 hover:bg-green-700 text-white font-bold h-14 text-base"
                    data-testid="share-whatsapp-btn"
                  >
                    <MessageCircle className="h-5 w-5 mr-2" />
                    Enviar via WhatsApp
                  </Button>
                  <Button
                    variant="outline"
                    onClick={copyToClipboard}
                    className="h-14 text-base font-medium"
                  >
                    <Copy className="h-5 w-5 mr-2" />
                    {copied ? "Copiado!" : "Copiar Link"}
                  </Button>
                </div>

                {/* Generate New */}
                <div className="text-center pt-2">
                  <Button
                    variant="ghost"
                    onClick={generateInviteLink}
                    disabled={isGenerating}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <Link2 className="h-4 w-4 mr-2" />
                    Gerar novo link
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* How it Works */}
        <Card className="bg-card/60 backdrop-blur-sm border-border/50 mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="h-5 w-5 text-primary" />
              Como funciona?
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold text-sm flex-shrink-0">
                  1
                </div>
                <div>
                  <p className="font-medium">Gere o link</p>
                  <p className="text-sm text-muted-foreground">Clique em &ldquo;Gerar Link de Convite&rdquo;</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold text-sm flex-shrink-0">
                  2
                </div>
                <div>
                  <p className="font-medium">Compartilhe via WhatsApp</p>
                  <p className="text-sm text-muted-foreground">Envie para os jogadores que deseja convidar</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold text-sm flex-shrink-0">
                  3
                </div>
                <div>
                  <p className="font-medium">Jogador se cadastra</p>
                  <p className="text-sm text-muted-foreground">Ele preenche nome, telefone, data de nascimento e posição</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-green-500/20 text-green-500 flex items-center justify-center font-bold text-sm flex-shrink-0">
                  ✓
                </div>
                <div>
                  <p className="font-medium">Pronto!</p>
                  <p className="text-sm text-muted-foreground">O jogador aparecerá na lista automaticamente</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Invites */}
        {recentInvites.length > 0 && (
          <Card className="bg-card/60 backdrop-blur-sm border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center justify-between text-lg">
                <span className="flex items-center gap-2">
                  <Share2 className="h-5 w-5 text-primary" />
                  Convites Recentes
                </span>
                <Badge variant="secondary">{recentInvites.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {recentInvites.map((invite, idx) => (
                  <div
                    key={invite.token || idx}
                    className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${
                        invite.status === 'registered' ? 'bg-green-500' : 'bg-yellow-500'
                      }`} />
                      <div>
                        <p className="font-medium text-sm">
                          {invite.player_name || invite.email || `Convite #${idx + 1}`}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(invite.created_at).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                    </div>
                    <Badge variant={invite.status === 'registered' ? 'default' : 'secondary'}>
                      {invite.status === 'registered' ? 'Cadastrado' : 'Pendente'}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
